import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, ChevronLeft, Coins, Cpu, Database, Diamond, Dot, FileSearch, Grid3x3, Home,
  Layers, LogOut, Moon, Package, Server,
  PanelsTopLeft, Search, Settings as SettingsIcon, Sun, Users, Languages, FlaskConical } from 'lucide-react';
import { api } from '../../lib/api';
import { T, TextEditToggle } from '../text/TextEdit';
import { useAuth } from '../../contexts/AuthContext';
import { setAppLanguage } from '../../lib/i18n';
import { formatCreditsOnly } from '../../lib/credits';
import { Skeleton } from '../pipeline/ui';
import { brandMark } from '../../lib/brand';
import {
  CommandDialog, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../ui/command';

// AppShell — the sidebar + header chrome every signed-in page renders inside.
//
// Ported from docs/ui/novamund-proto.html (shell copied from satnaing/shadcn-admin). The mock is a
// picture; its seven rules are the spec. Two of them shape this file:
//
//   Rule 2 — strings are KEYS. Every label here goes through t(); no literal copy is lifted out of
//   the mock. Rule 6 — counts are data. Nothing here is driven by how many items the mock drew.
//
// DELIBERATELY PARTIAL, and this is the whole discipline of stage 1: the sidebar renders only the
// destinations that EXIST today. The mock also shows Ideas / Market analyses / Build packs and a
// five-way admin split; those arrive with their pages, in their own stages. A nav item pointing at
// a route that does not exist is a dead control, and drawing one would be the same mistake the
// mock's own R1 was written about.

// Persisted outside React so the choice survives a reload. The .dark token block already exists in
// index.css — this only decides whether the class is on <html>.
const THEME_KEY = 'app_theme';

function applyTheme(theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

function readTheme() {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function initials(email) {
  if (!email) return '··';
  const name = String(email).split('@')[0] || '';
  return (name.slice(0, 2) || '··').toUpperCase();
}

// One sidebar row. `active` is decided by the caller so the match rule stays visible there rather
// than hiding inside a component.
function NavItem({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-current={active ? 'page' : undefined}
      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-start text-[13.5px] transition-colors
        ${active ? 'bg-accent font-bold text-accent-foreground' : 'text-foreground hover:bg-accent'}`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-accent-foreground' : 'text-muted-foreground'}`} />
      <span className="truncate">{label}</span>
    </button>
  );
}

function NavGroup({ children }) {
  return <div className="px-2.5 pb-1.5 pt-3.5 text-[11.5px] font-semibold text-muted-foreground">{children}</div>;
}

// Credits live in the header rather than the sidebar. The mock's header does not show them, but the
// live app's project header did, and R5 is explicit: absence from the mock is a gap in the mock, not
// a delete instruction. Moving it is a restyle; dropping it would be a silent feature deletion.
function WalletBadge({ wallet }) {
  const { t } = useTranslation();
  return (
    <div className="hidden items-center gap-1.5 font-data text-sm text-muted-foreground sm:flex" data-testid="wallet-badge">
      <Coins className="h-4 w-4 text-warning" />
      {wallet ? t('wallet.credits', { balance: formatCreditsOnly(wallet.balance_credits ?? 0) }) : <Skeleton className="h-4 w-14" />}
    </div>
  );
}

export default function AppShell({ children, breadcrumbs = null }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();

  const [collapsed, setCollapsed] = useState(false);
  const [theme, setTheme] = useState(readTheme);
  const [isAdmin, setIsAdmin] = useState(false);
  // 049 limited admin: same admin surface minus the cost side (models) and the corpus engine.
  const [isLimitedAdmin, setIsLimitedAdmin] = useState(false);
  const [walletInfo, setWalletInfo] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Settings is a parent that expands, as the mock draws it. Open by default: its children are
  // the only way to reach billing and the model settings.
  const [setOpen, setSetOpen] = useState(true);
  // Path B's row must not appear where Path B is off - that would be a dead control.
  const [pathBOn, setPathBOn] = useState(false);
  const [deskOn, setDeskOn] = useState(false);

  useEffect(() => { applyTheme(theme); }, [theme]);

  useEffect(() => {
    // Both are best-effort chrome. A failure here must never blank the page the shell is wrapping,
    // so neither rejection propagates.
    api.getMe().then((r) => {
      setIsAdmin(!!r?.is_admin);
      setIsLimitedAdmin(r?.is_limited_admin === true);
    }).catch(() => { setIsAdmin(false); setIsLimitedAdmin(false); });
    api.getWallet().then(setWalletInfo).catch(() => {});
    // The same strict test the door picker uses, so the row and the door always agree.
    api.getManifest()
      .then((m) => { setPathBOn(m?.entitlements?.paths?.path_b === true); setDeskOn(m?.entitlements?.paths?.desk === true); })
      .catch(() => setPathBOn(false));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem(THEME_KEY, next); } catch { /* private mode — theme is per-session */ }
      return next;
    });
  }, []);

  const goto = useCallback((to) => { setPaletteOpen(false); navigate(to); }, [navigate]);

  // Exact match for '/', prefix match for the rest — '/admin/tenants' must not light up '/'.
  // '/' and '/settings' need an EXACT match: with a prefix test, '/settings' would stay lit
  // while '/settings/billing' is open and two rows would look active at once.
  const EXACT = ['/', '/settings'];
  const at = (to) => (EXACT.includes(to) ? pathname === to : pathname.startsWith(to));

  const destinations = [
    { to: '/', icon: Home, label: t('home.title'), testId: 'nav-home', show: true },
    // TEMPORARY. The design has no "project" in the UI and no projects list; the Ideas dashboard
    // replaces this. It stays until that exists, because right now it is the only route that opens
    // an existing idea — removing it first would strand every project in the app.
    { to: '/ideas', icon: Diamond, label: t('stepper.idea'), testId: 'nav-ideas', show: true },
    { to: '/markets', icon: Grid3x3, label: t('stepper.market'), testId: 'nav-markets', show: true },
    { to: '/packs', icon: Package, label: t('stepper.build'), testId: 'nav-packs', show: true },
    // Path B sits with the other artifacts, as the mock draws it. It opens a list of the
    // reports that exist; the screen behind each one is unchanged.
    { to: '/pathb', icon: FileSearch, label: t('stepper.pathb'), testId: 'nav-pathb', show: pathBOn },
    // The desk (engine Phase 2): gated by its own entitlement key like Path B above.
    { to: '/desk', icon: FlaskConical, label: t('nav.desk'), testId: 'nav-desk', show: deskOn },
    // The admin surface was one route stacking 21 sections. These five are the split the mock
    // draws; the keys have existed since 2026-08-01 and were unused until now. Corpus keeps its
    // own row - it is a different page, not part of that split.
    { to: '/admin/tenants', icon: Server, label: t('nav.adminTenants'), testId: 'nav-tenant-admin', show: isAdmin || isLimitedAdmin },
    { to: '/admin/users', icon: Users, label: t('nav.adminUsers'), testId: 'nav-admin-users', show: isAdmin || isLimitedAdmin },
    { to: '/admin/models', icon: Cpu, label: t('nav.adminModels'), testId: 'nav-admin-models', show: isAdmin },
    { to: '/admin/credits', icon: Coins, label: t('nav.adminCredit'), testId: 'nav-admin-credits', show: isAdmin || isLimitedAdmin },
    // 067: the interface wording, editable in the app. Superadmin only — it changes what EVERY
    // user reads, so it is not a limited-admin surface.
    { to: '/admin/text', icon: Languages, label: t('nav.adminText'), testId: 'nav-admin-text', show: isAdmin },
    { to: '/admin/dimensions', icon: Layers, label: t('nav.adminPathb'), testId: 'nav-admin-dimensions', show: isAdmin || isLimitedAdmin },
    // CA6: every user reaches this page — a user to manage HIS OWN RAGFlow connection, a super
    // admin for the full corpus surface. The page itself draws only what the tier allows.
    { to: '/admin/corpus', icon: Database, label: isAdmin ? t('nav.corpusAdmin') : t('corpusAdmin.myCorporaTitle'), testId: 'nav-corpus-admin', show: true },    // Settings is three sections, as the mock splits it. They are separate destinations rather than
    // in-page tabs so each is linkable and the sidebar can show which one is open.
    { to: '/settings', icon: SettingsIcon, label: t('settings.tabProfile'), testId: 'nav-settings', show: true },
    { to: '/settings/billing', icon: Coins, label: t('settings.tabBilling'), testId: 'nav-settings-billing', show: true },
    { to: '/settings/llm', icon: Cpu, label: t('settings.tabModel'), testId: 'nav-settings-llm', show: true },
  ].filter((d) => d.show);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside
        data-testid="app-sidebar"
        className={`sticky top-0 flex h-screen shrink-0 flex-col overflow-y-auto border-e border-border bg-card
          transition-[width] duration-150 ${collapsed ? 'w-0 overflow-hidden border-none' : 'w-[248px]'}`}
      >
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2.5 border-b border-border p-3.5 text-start"
          data-testid="sidebar-brand"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary font-heading text-sm font-extrabold text-primary-foreground">
            {brandMark(t('app.name'))}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-heading text-[13.5px] font-bold leading-tight"><T k="app.name" /></span>
            <span className="block truncate text-[11.5px] leading-tight text-muted-foreground">{user?.email}</span>
          </span>
        </button>

        <nav className="flex-1 p-2">
          <NavGroup><T k="nav.home" /></NavGroup>
          {/* startsWith, not !==. Excluding '/settings' by exact match let /settings/billing
              and /settings/llm fall into this group as well as their own - two rows each. */}
          {destinations.filter((d) => !d.to.startsWith('/admin') && !d.to.startsWith('/settings')).map((d) => (
            <NavItem key={d.to} icon={d.icon} label={d.label} active={at(d.to)} onClick={() => goto(d.to)} testId={d.testId} />
          ))}

          {(isAdmin || isLimitedAdmin) && <NavGroup><T k="nav.admin" /></NavGroup>}
          {destinations.filter((d) => d.to.startsWith('/admin')).map((d) => (
            <NavItem key={d.to} icon={d.icon} label={d.label} active={at(d.to)} onClick={() => goto(d.to)} testId={d.testId} />
          ))}

          {/* A parent that expands, children indented behind a rule - shadcn-admin's settings
              group, which is what the mock copies. */}
          <NavGroup><T k="nav.settings" /></NavGroup>
          <button
            type="button"
            onClick={() => setSetOpen((v) => !v)}
            data-testid="nav-settings-group"
            aria-expanded={setOpen}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-start text-[13.5px] text-foreground transition-colors hover:bg-accent"
          >
            <SettingsIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate"><T k="nav.settings" /></span>
            {setOpen
              ? <ChevronDown className="ms-auto h-3.5 w-3.5 text-muted-foreground" />
              : <ChevronLeft className="ms-auto h-3.5 w-3.5 text-muted-foreground" />}
          </button>
          {setOpen && (
            <div className="ms-3.5 border-s border-border ps-1.5">
              {destinations.filter((d) => d.to.startsWith('/settings')).map((d) => (
                <NavItem key={d.to} icon={Dot} label={d.label} active={at(d.to)} onClick={() => goto(d.to)} testId={d.testId} />
              ))}
            </div>
          )}
          <NavItem icon={LogOut} label={<T k="nav.signOut" />} active={false} onClick={signOut} testId="sign-out-button" />
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center gap-3.5 border-b border-border bg-background px-5 py-3">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label={<T k="nav.home" />}
            data-testid="sidebar-toggle"
          >
            <PanelsTopLeft className="h-4 w-4" />
          </button>

          <div className="flex min-w-0 gap-1" data-testid="breadcrumbs">{breadcrumbs}</div>

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="ms-auto hidden w-[250px] items-center gap-2 rounded-lg border border-input bg-background px-2.5 py-1.5 text-[13px] text-muted-foreground md:flex"
            data-testid="command-open"
          >
            <Search className="h-3.5 w-3.5" />
            <span><T k="nav.search" /></span>
            <span className="ms-auto rounded border border-border px-1.5 font-data text-[10.5px]">⌘K</span>
          </button>

          {/* 067: the wording editor's toggle. Renders nothing for a non-superadmin — turning it on
              changes what EVERY user reads, so it is not a limited-admin control. */}
          <TextEditToggle />

          <WalletBadge wallet={walletInfo?.wallet} />

          <button
            type="button"
            onClick={toggleTheme}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </button>

          {/* The fa/EN toggle is gone (Persian lock, Ali 2026-08-04) — the UI is fa-only. */}
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted font-data text-[11px] font-bold text-muted-foreground">
            {initials(user?.email)}
          </span>
        </header>

        <main className="w-full max-w-[1180px] px-6 pb-24 pt-6">{children}</main>
      </div>

      <CommandDialog open={paletteOpen} onOpenChange={setPaletteOpen}>
        <CommandInput placeholder={t('nav.search')} />
        {/* No empty state: the mock's palette has none, and inventing the string it would need
            means inventing a locale key the ledger never listed. */}
        <CommandList>
          <CommandGroup>
            {destinations.map((d) => (
              <CommandItem key={d.to} value={d.label} onSelect={() => goto(d.to)}>
                <d.icon className="me-2 h-4 w-4" />
                {d.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
  );
}
